import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Chip } from '@weareopenr/component-library';
import { DataTable } from '../../components/DataTable/DataTable';
import {
  useContacts,
  useInvoiceMutations,
  useInvoicePreview,
  useInvoices,
  usePermissions,
} from '../../hooks';
import { isMockMode } from '../../constants';
import type { IInvoice, IInvoiceInput, IInvoiceLine, ITenantSummary } from '../../types';
import {
  fmtMoney,
  isoToday,
  longDate,
  shortDate,
  statusColour,
  statusLabel,
} from '../Invoices/format';
import styles from './TenantInvoices.module.scss';

const round2 = (value: number) => Math.round(value * 100) / 100;
const apiError = (error: unknown) => (error as { message?: string } | null)?.message ?? null;
/** Stable empty arrays: a fresh `[]` default per render would re-run memos and effects forever. */
const NO_CONTACTS: never[] = [];
const NO_INVOICES: never[] = [];

// ---------- editor (new draft or edit draft) ----------

interface EditorProps {
  tenant: ITenantSummary;
  existing: IInvoice | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (input: IInvoiceInput) => void;
}

const emptyLine = (): IInvoiceLine => ({
  description: '',
  product_code: null,
  quantity: 1,
  unit_price: 0,
  amount: 0,
});

const Editor = ({ tenant, existing, saving, error, onClose, onSave }: EditorProps) => {
  const [periodStart, setPeriodStart] = useState(existing?.period_start ?? '');
  const [periodEnd, setPeriodEnd] = useState(existing?.period_end ?? '');
  const [issuedOn, setIssuedOn] = useState(existing?.issued_on ?? isoToday());
  const [number, setNumber] = useState(existing?.number ?? '');
  const [terms, setTerms] = useState<string>(existing ? String(existing.payment_terms_days) : '');
  const [vatRate, setVatRate] = useState<string>(existing ? String(existing.vat_rate) : '');
  const [lines, setLines] = useState<IInvoiceLine[]>(existing?.lines ?? []);
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [accountNumber, setAccountNumber] = useState(existing?.account_number ?? '');
  const [billingName, setBillingName] = useState(existing?.billing_name ?? '');
  const [billingAddress, setBillingAddress] = useState(existing?.billing_address ?? '');
  const [touchedLines, setTouchedLines] = useState(Boolean(existing));

  // For a new invoice the API proposes the period, the lines and the next number.
  const { data: preview, isFetching } = useInvoicePreview(
    tenant.name,
    {
      period_start: periodStart || undefined,
      period_end: periodEnd || undefined,
      issued_on: issuedOn || undefined,
    },
    !existing,
  );
  useEffect(() => {
    if (existing || !preview) return;
    if (!periodStart) setPeriodStart(preview.period_start);
    if (!periodEnd) setPeriodEnd(preview.period_end);
    if (!number) setNumber(preview.next_number);
    if (terms === '') setTerms(String(preview.payment_terms_days));
    if (vatRate === '') setVatRate(String(preview.vat_rate));
    if (!touchedLines) setLines(preview.lines);
    if (!billingName) setBillingName(preview.billing?.billing_name ?? tenant.display_name);
    if (!billingAddress) setBillingAddress(preview.billing?.billing_address ?? '');
    if (!accountNumber) setAccountNumber(preview.billing?.account_number ?? '');
  }, [
    preview,
    existing,
    periodStart,
    periodEnd,
    number,
    terms,
    vatRate,
    touchedLines,
    billingName,
    billingAddress,
    accountNumber,
    tenant.display_name,
  ]);

  const setLine = (index: number, patch: Partial<IInvoiceLine>) => {
    setTouchedLines(true);
    setLines((current) =>
      current.map((line, i) => {
        if (i !== index) return line;
        const next = { ...line, ...patch };
        return { ...next, amount: round2(next.quantity * next.unit_price) };
      }),
    );
  };
  const removeLine = (index: number) => {
    setTouchedLines(true);
    setLines((current) => current.filter((_, i) => i !== index));
  };
  const addLine = () => {
    setTouchedLines(true);
    setLines((current) => [...current, emptyLine()]);
  };
  const resetLines = () => {
    setTouchedLines(false);
    if (preview) setLines(preview.lines);
  };

  const subtotal = round2(lines.reduce((sum, line) => sum + line.amount, 0));
  const vat = round2(subtotal * ((Number(vatRate) || 0) / 100));
  const valid =
    periodStart &&
    periodEnd &&
    periodEnd >= periodStart &&
    lines.length > 0 &&
    lines.every((line) => line.description.trim());

  return (
    <aside
      className={styles.panel}
      aria-label={existing ? `Edit invoice ${existing.number}` : 'New invoice'}
    >
      <header className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          {existing ? `Edit draft ${existing.number}` : 'New invoice'}
        </h2>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid) return;
          onSave({
            number: number.trim() || null,
            period_start: periodStart,
            period_end: periodEnd,
            issued_on: issuedOn,
            payment_terms_days: terms === '' ? null : Number(terms),
            vat_rate: vatRate === '' ? null : Number(vatRate),
            lines: lines.map((line) => ({ ...line, description: line.description.trim() })),
            notes: notes.trim() || null,
            account_number: accountNumber.trim() || null,
            billing_name: billingName.trim() || null,
            billing_address: billingAddress.trim() || null,
          });
        }}
      >
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Invoice number</span>
            <input
              value={number}
              onChange={(event) => setNumber(event.target.value)}
              placeholder={preview?.next_number ?? ''}
            />
            <small>Proposed by Nexus; change it if accounts has a different one.</small>
          </label>
          <label className={styles.field}>
            <span>Invoice date</span>
            <input
              type="date"
              value={issuedOn}
              onChange={(event) => setIssuedOn(event.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span>Account number</span>
            <input
              value={accountNumber}
              onChange={(event) => setAccountNumber(event.target.value)}
            />
          </label>
        </div>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Period from</span>
            <input
              type="date"
              value={periodStart}
              onChange={(event) => setPeriodStart(event.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span>Period to</span>
            <input
              type="date"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span>Terms (days) / VAT %</span>
            <span className={styles.pair}>
              <input
                type="number"
                min={0}
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
                aria-label="Payment terms in days"
              />
              <input
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={vatRate}
                onChange={(event) => setVatRate(event.target.value)}
                aria-label="VAT rate"
              />
            </span>
          </label>
        </div>
        <div className={styles.row2}>
          <label className={styles.field}>
            <span>Invoice to</span>
            <input value={billingName} onChange={(event) => setBillingName(event.target.value)} />
          </label>
          <label className={styles.field}>
            <span>Billing address</span>
            <textarea
              rows={3}
              value={billingAddress}
              onChange={(event) => setBillingAddress(event.target.value)}
            />
          </label>
        </div>

        <div className={styles.linesHead}>
          <span>Lines</span>
          <span className={styles.linesHint}>
            {isFetching && !existing ? 'Working out lines from subscriptions…' : null}
            {!existing && touchedLines && preview ? (
              <button type="button" className={styles.linkBtn} onClick={resetLines}>
                Reset to subscriptions
              </button>
            ) : null}
          </span>
        </div>
        {lines.length === 0 ? (
          <p className={styles.muted}>
            No lines.{' '}
            {existing
              ? ''
              : 'There are no priced subscriptions in this period, so add lines by hand.'}
          </p>
        ) : null}
        {lines.map((line, index) => (
          <div key={index} className={styles.line}>
            <input
              className={styles.lineDesc}
              value={line.description}
              onChange={(event) => setLine(index, { description: event.target.value })}
              placeholder="Description"
              aria-label={`Line ${index + 1} description`}
              required
            />
            <input
              className={styles.lineNum}
              type="number"
              min={0}
              step="1"
              value={line.quantity}
              onChange={(event) => setLine(index, { quantity: Number(event.target.value) })}
              aria-label={`Line ${index + 1} quantity`}
            />
            <input
              className={styles.lineNum}
              type="number"
              step="0.01"
              value={line.unit_price}
              onChange={(event) => setLine(index, { unit_price: Number(event.target.value) })}
              aria-label={`Line ${index + 1} unit price`}
            />
            <span className={styles.lineAmount}>{fmtMoney(line.amount)}</span>
            <button
              type="button"
              className={styles.remove}
              onClick={() => removeLine(index)}
              aria-label={`Remove line ${index + 1}`}
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className={styles.linkBtn} onClick={addLine}>
          + Add line
        </button>

        <label className={styles.field}>
          <span>Notes on the invoice</span>
          <textarea
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="PO reference, what the period covers, anything the customer should see"
          />
        </label>

        <div className={styles.totals}>
          <span>Net</span>
          <span>{fmtMoney(subtotal)}</span>
          <span>VAT {vatRate || 0}%</span>
          <span>{fmtMoney(vat)}</span>
          <span className={styles.grand}>Total</span>
          <span className={styles.grand}>{fmtMoney(round2(subtotal + vat))}</span>
        </div>
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={!valid || saving}>
            {saving ? 'Saving…' : existing ? 'Save draft' : 'Create draft'}
          </button>
        </div>
      </form>
    </aside>
  );
};

// ---------- detail / actions ----------

interface DetailProps {
  tenant: ITenantSummary;
  invoice: IInvoice;
  onClose: () => void;
  onEdit: () => void;
}

const Detail = ({ tenant, invoice, onClose, onEdit }: DetailProps) => {
  const { issue, send, paid, void: voidIt, pdf } = useInvoiceMutations(tenant.name);
  const { data: contacts = NO_CONTACTS } = useContacts(tenant.name);
  const { canBill } = usePermissions();
  const billingContacts = useMemo(
    () => contacts.filter((contact) => contact.roles.includes('billing')),
    [contacts],
  );
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState<string[]>([]);
  const [extra, setExtra] = useState('');
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [paidOn, setPaidOn] = useState(isoToday());
  const [voidReason, setVoidReason] = useState('');
  const [confirmVoid, setConfirmVoid] = useState(false);

  const billingEmails = billingContacts.map((contact) => contact.email).join(',');
  useEffect(() => {
    setTo(billingEmails ? billingEmails.split(',') : []);
  }, [billingEmails]);

  const busy =
    issue.isLoading || send.isLoading || paid.isLoading || voidIt.isLoading || pdf.isLoading;
  const error =
    apiError(issue.error) ??
    apiError(send.error) ??
    apiError(paid.error) ??
    apiError(voidIt.error) ??
    apiError(pdf.error);
  const open = invoice.status === 'issued' || invoice.status === 'sent';
  const viewPath = `/tenants/${encodeURIComponent(tenant.name)}/invoices/${encodeURIComponent(
    invoice.id,
  )}/view`;

  const download = () =>
    pdf.mutate(
      { id: invoice.id, download: true },
      { onSuccess: ({ data }) => window.open(data.url, '_blank', 'noopener') },
    );

  const recipients = [
    ...to,
    ...extra
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean),
  ];
  const doSend = () =>
    send.mutate(
      {
        id: invoice.id,
        input: {
          to: Array.from(new Set(recipients)),
          cc: cc
            .split(',')
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean),
          message: message.trim() || null,
        },
      },
      { onSuccess: () => setSending(false) },
    );

  return (
    <aside className={styles.panel} aria-label={`Invoice ${invoice.number}`}>
      <header className={styles.panelHeader}>
        <div>
          <h2 className={styles.panelTitle}>Invoice {invoice.number}</h2>
          <div className={styles.panelMeta}>
            <Chip label={statusLabel(invoice)} backgroundColour={statusColour(invoice)} />
            <span>{fmtMoney(invoice.total, invoice.currency)}</span>
            <span>due {longDate(invoice.due_on)}</span>
          </div>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <dl className={styles.dl}>
        <dt>Period</dt>
        <dd>
          {longDate(invoice.period_start)} to {longDate(invoice.period_end)}
        </dd>
        <dt>Invoice date</dt>
        <dd>{longDate(invoice.issued_on)}</dd>
        <dt>Invoice to</dt>
        <dd>
          {invoice.billing_name}
          {invoice.account_number ? ` · account ${invoice.account_number}` : ''}
        </dd>
        {invoice.sent_at ? (
          <>
            <dt>Emailed</dt>
            <dd>
              {new Date(invoice.sent_at).toLocaleString('en-GB')} to {invoice.sent_to.join(', ')}
              {invoice.sent_cc.length ? ` (cc ${invoice.sent_cc.join(', ')})` : ''}
            </dd>
          </>
        ) : null}
        {invoice.paid_on ? (
          <>
            <dt>Paid on</dt>
            <dd>{longDate(invoice.paid_on)}</dd>
          </>
        ) : null}
        {invoice.void_reason ? (
          <>
            <dt>Void reason</dt>
            <dd>{invoice.void_reason}</dd>
          </>
        ) : null}
      </dl>

      <table className={styles.lineTable}>
        <tbody>
          {invoice.lines.map((line, index) => (
            <tr key={`${line.description}-${index}`}>
              <td>{line.description}</td>
              <td className={styles.num}>
                {line.quantity} × {line.unit_price.toFixed(2)}
              </td>
              <td className={styles.num}>{fmtMoney(line.amount, invoice.currency)}</td>
            </tr>
          ))}
          <tr className={styles.sub}>
            <td colSpan={2}>Net</td>
            <td className={styles.num}>{fmtMoney(invoice.subtotal, invoice.currency)}</td>
          </tr>
          <tr className={styles.sub}>
            <td colSpan={2}>VAT {invoice.vat_rate}%</td>
            <td className={styles.num}>{fmtMoney(invoice.vat_amount, invoice.currency)}</td>
          </tr>
          <tr className={styles.grandRow}>
            <td colSpan={2}>Total</td>
            <td className={styles.num}>{fmtMoney(invoice.total, invoice.currency)}</td>
          </tr>
        </tbody>
      </table>

      <div className={styles.actionGrid}>
        <Link to={viewPath} className={styles.ghost}>
          View
        </Link>
        <button
          type="button"
          className={styles.ghost}
          onClick={download}
          disabled={busy || isMockMode()}
          title={isMockMode() ? 'PDF download needs the real API' : undefined}
        >
          Download PDF
        </button>
        {canBill && invoice.status === 'draft' ? (
          <>
            <button type="button" className={styles.ghost} onClick={onEdit} disabled={busy}>
              Edit
            </button>
            <button
              type="button"
              className={styles.primary}
              onClick={() => issue.mutate(invoice.id)}
              disabled={busy}
            >
              {issue.isLoading ? 'Issuing…' : 'Issue'}
            </button>
          </>
        ) : null}
        {canBill && open ? (
          <>
            <button
              type="button"
              className={styles.primary}
              onClick={() => setSending(true)}
              disabled={busy}
            >
              {invoice.status === 'sent' ? 'Send again' : 'Send'}
            </button>
            <span className={styles.inline}>
              <input
                type="date"
                value={paidOn}
                onChange={(event) => setPaidOn(event.target.value)}
                aria-label="Paid on"
              />
              <button
                type="button"
                className={styles.ghost}
                onClick={() => paid.mutate({ id: invoice.id, paidOn })}
                disabled={busy}
              >
                Mark paid
              </button>
            </span>
          </>
        ) : null}
        {canBill && invoice.status !== 'void' && invoice.status !== 'paid' ? (
          confirmVoid ? (
            <span className={styles.inline}>
              <input
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
                placeholder="reason"
                aria-label="Void reason"
              />
              <button
                type="button"
                className={styles.danger}
                onClick={() =>
                  voidIt.mutate(
                    { id: invoice.id, reason: voidReason.trim() || null },
                    { onSuccess: () => setConfirmVoid(false) },
                  )
                }
                disabled={busy}
              >
                Confirm void
              </button>
              <button type="button" className={styles.ghost} onClick={() => setConfirmVoid(false)}>
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={styles.dangerGhost}
              onClick={() => setConfirmVoid(true)}
              disabled={busy}
            >
              Void
            </button>
          )
        ) : null}
      </div>

      {sending ? (
        <div className={styles.sendBox} role="dialog" aria-label="Send invoice">
          <strong>Email invoice {invoice.number}</strong>
          {billingContacts.length === 0 ? (
            <p className={styles.warn}>
              No billing contact on this tenant. Add one on the Contacts tab or type an address
              below.
            </p>
          ) : null}
          {billingContacts.map((contact) => (
            <label key={contact.id} className={styles.check}>
              <input
                type="checkbox"
                checked={to.includes(contact.email)}
                onChange={(event) =>
                  setTo((current) =>
                    event.target.checked
                      ? [...current, contact.email]
                      : current.filter((email) => email !== contact.email),
                  )
                }
              />
              <span>
                {contact.name} <span className={styles.muted}>{contact.email}</span>
              </span>
            </label>
          ))}
          <label className={styles.field}>
            <span>Other recipients (comma separated)</span>
            <input
              value={extra}
              onChange={(event) => setExtra(event.target.value)}
              placeholder="ap@customer.example"
            />
          </label>
          <label className={styles.field}>
            <span>Cc</span>
            <input
              value={cc}
              onChange={(event) => setCc(event.target.value)}
              placeholder="finance@weareopenr.com"
            />
          </label>
          <label className={styles.field}>
            <span>Message (optional, goes above the standard text)</span>
            <textarea
              rows={3}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>
          {tenant.environment !== 'prod' ? (
            <p className={styles.warn}>
              This is {tenant.environment}: only @weareopenr.com addresses are accepted, so a test
              never reaches a customer.
            </p>
          ) : null}
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={() => setSending(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primary}
              onClick={doSend}
              disabled={busy || recipients.length === 0}
            >
              {send.isLoading
                ? 'Sending…'
                : `Send to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      ) : null}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </aside>
  );
};

// ---------- tab ----------

export const TenantInvoices = ({ tenant }: { tenant: ITenantSummary }) => {
  const { data: invoices = NO_INVOICES, isLoading, error } = useInvoices(tenant.name);
  const { create, update } = useInvoiceMutations(tenant.name);
  const { canBill } = usePermissions();
  const [mode, setMode] = useState<'closed' | 'new' | 'view' | 'edit'>('closed');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = invoices.find((invoice) => invoice.id === selectedId) ?? null;

  const columns = useMemo<ColumnDef<IInvoice, any>[]>(
    () => [
      {
        header: 'Number',
        accessorKey: 'number',
        size: 100,
        cell: ({ getValue }) => <strong>{getValue<string>()}</strong>,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 110,
        cell: ({ row }) => (
          <Chip label={statusLabel(row.original)} backgroundColour={statusColour(row.original)} />
        ),
      },
      {
        header: 'Period',
        accessorKey: 'period_start',
        size: 200,
        cell: ({ row }) =>
          `${shortDate(row.original.period_start)} to ${shortDate(row.original.period_end)}`,
      },
      {
        header: 'Issued',
        accessorKey: 'issued_on',
        size: 100,
        cell: ({ getValue }) => shortDate(getValue<string>()),
      },
      {
        header: 'Due',
        accessorKey: 'due_on',
        size: 100,
        cell: ({ getValue }) => shortDate(getValue<string>()),
      },
      {
        header: 'Total',
        accessorKey: 'total',
        size: 110,
        meta: { align: 'right' },
        cell: ({ row }) => fmtMoney(row.original.total, row.original.currency),
      },
      {
        header: 'Sent to',
        accessorKey: 'sent_to',
        enableSorting: false,
        cell: ({ getValue }) => {
          const sent = getValue<string[]>();
          return sent.length ? sent.join(', ') : <span className={styles.muted}>not sent</span>;
        },
      },
    ],
    [],
  );

  const outstanding = invoices.filter(
    (invoice) => invoice.status === 'issued' || invoice.status === 'sent',
  );
  const overdue = outstanding.filter((invoice) => invoice.overdue);
  const owed = outstanding.reduce((sum, invoice) => sum + invoice.total, 0);

  if (isLoading) return <p className={styles.muted}>Loading invoices…</p>;
  if (error)
    return <p className={styles.error}>Could not load: {apiError(error) ?? 'unknown error'}</p>;

  const openPanel = mode !== 'closed';

  return (
    <div className={openPanel ? styles.split : undefined}>
      <div className={styles.main}>
        <div className={styles.topRow}>
          <p className={styles.muted}>
            {invoices.length} invoice{invoices.length === 1 ? '' : 's'}.{' '}
            {outstanding.length > 0 ? (
              <>
                {fmtMoney(owed)} outstanding across {outstanding.length}.{' '}
                {overdue.length > 0 ? (
                  <span className={styles.warn}>{overdue.length} overdue.</span>
                ) : null}
              </>
            ) : (
              'Nothing outstanding.'
            )}
          </p>
          {canBill ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                setSelectedId(null);
                setMode('new');
              }}
            >
              + New invoice
            </button>
          ) : null}
        </div>
        <DataTable
          columns={columns}
          data={invoices}
          storageKey="nexus-ui.tenant.invoices"
          onRowClick={(row) => {
            setSelectedId(row.id);
            setMode('view');
          }}
          emptyMessage="No invoices yet. New invoice proposes the next period from the billing cycle and the lines from subscriptions."
        />
      </div>
      {mode === 'new' ? (
        <Editor
          tenant={tenant}
          existing={null}
          saving={create.isLoading}
          error={apiError(create.error)}
          onClose={() => setMode('closed')}
          onSave={(input) =>
            create.mutate(input, {
              onSuccess: ({ data }) => {
                setSelectedId(data.id);
                setMode('view');
              },
            })
          }
        />
      ) : null}
      {mode === 'edit' && selected ? (
        <Editor
          tenant={tenant}
          existing={selected}
          saving={update.isLoading}
          error={apiError(update.error)}
          onClose={() => setMode('view')}
          onSave={(input) =>
            update.mutate({ id: selected.id, input }, { onSuccess: () => setMode('view') })
          }
        />
      ) : null}
      {mode === 'view' && selected ? (
        <Detail
          tenant={tenant}
          invoice={selected}
          onClose={() => setMode('closed')}
          onEdit={() => setMode('edit')}
        />
      ) : null}
    </div>
  );
};
